<template>
    <div>
        <g-loader v-if="loading" />
        <div v-else>
            <g-empty-state
                v-if="showNullState"
                :image-link="INSIGHT_NULL_STATE"
                title="No performance activity to show"
            >
                <p>
                    Try sharing your affiliate link or discount code with your audience.
                    <a :href="FAQ_MANAGING_INSIGHTS" target="_blank">Learn more</a>
                </p>
            </g-empty-state>
            <div v-else>
                <g-typography class="g-mb-6" tag="h1" font-weight="semibold" value="Insights" />
                <g-typography tag="h4" font-weight="semibold">Your Conversion Activity</g-typography>
                <g-typography class="g-mt-3 g-mb-3" tag="p" color="grey-6">
                    This information makes it easy to measure the performance of your activity.
                </g-typography>
                <g-typography class="g-mt-3 g-mb-5" tag="p" color="grey-6">
                    <a href="https://help.grin.co/v1/docs/managing-insights" target="_blank">Need help?</a> Visit our
                    FAQ for support.
                </g-typography>

                <conversions-chart class="g-mb-5" />

                <key-value-data-table :data="conversionData" class="g-mb-5" />
                <key-value-data-table :data="linkData" class="g-mb-5" />
                <key-value-data-table :data="commissionData" />
            </div>
        </div>
    </div>
</template>

<script>
    import { INSIGHT_NULL_STATE } from '../../constants/cdn-assets';
    import { FAQ_MANAGING_INSIGHTS } from '../../constants/faqs';
    import KeyValueDataTable from '../library/KeyValueDataTable.vue';
    import ConversionsChart from './partials/ConversionsChart.vue';
    import useInsights from '../../../src/composables/use-insights';
    import useCurrentInstance from '../../../src/helpers/useCurrentInstance';
    import { useContactStore } from '../../../stores';
    import useRouter from '../../../src/composables/use-router';
    import { message } from '@grininc/grin-ui';
    import { ref, onMounted } from 'vue-demi';

    export default {
        name: 'Insights',
        components: {
            KeyValueDataTable,
            ConversionsChart
        },
        setup() {
            const loading = ref(false);
            const conversionData = ref(null);
            const linkData = ref(null);
            const commissionData = ref(null);
            const showNullState = ref(null);
            const router = useRouter().get();
            const currentInstance = useCurrentInstance().$root;

            onMounted(() => {
                fetchMetrics();
            });

            // eslint-disable-next-line complexity
            const fetchMetrics = async () => {
                loading.value = true;

                const contact = useContactStore().data;
                if (
                    (contact.has_discount_code === true || contact.has_affiliate_link === true) &&
                    currentInstance.account_show_insights_on_live_url
                ) {
                    try {
                        const metricsResponse = await useInsights().getMetrics(contact.id);
                        const metrics = metricsResponse.data;

                        const allLinks = metrics.all_conversions_links ?? 0;
                        const allCodes = metrics.all_conversions_codes ?? 0;
                        const totalConversions = parseInt(allLinks) + parseInt(allCodes);

                        const linkVisits = metrics.all_link_visits ?? 0;
                        const uniqueLinkVisits = metrics.all_unique_link_visits ?? 0;
                        const conversionRate =
                            parseFloat(uniqueLinkVisits) && parseFloat(uniqueLinkVisits) > 0
                                ? ((parseFloat(totalConversions) / parseFloat(uniqueLinkVisits)) * 100).toFixed(1) + '%'
                                : '0%';

                        const totalCommissions = metrics.total_commissions ?? '$0';
                        const outstandingCommissions = metrics.outstanding_commissions ?? '$0';
                        const paidCommissions = metrics.paid_commissions ?? '$0';

                        conversionData.value = {
                            'Total Conversions': totalConversions.toLocaleString('en-US'),
                            'All links': allLinks.toLocaleString('en-US'),
                            'All codes': allCodes.toLocaleString('en-US')
                        };

                        linkData.value = {
                            'Conversion Rate': conversionRate,
                            'All link visits': linkVisits.toLocaleString('en-US'),
                            'All unique link visits': uniqueLinkVisits.toLocaleString('en-US')
                        };

                        commissionData.value = {
                            'Total Commissions': totalCommissions,
                            Outstanding: outstandingCommissions,
                            Paid: paidCommissions
                        };

                        showNullState.value =
                            metrics.all_conversions_codes || metrics.all_conversions_links || metrics.all_link_visits
                                ? false
                                : true;
                    } catch (e) {
                        message.error('An error occurred while retrieving your insights.');
                    }
                } else {
                    router.push({ name: 'sponsorships' });
                }
                loading.value = false;
            };

            return {
                loading,
                conversionData,
                linkData,
                commissionData,
                showNullState,
                INSIGHT_NULL_STATE,
                FAQ_MANAGING_INSIGHTS
            };
        }
    };
</script>
